import type { ChangeEvent, RefObject } from "react";
import type { PaceBand, TrainingPlanLibrary } from "../../../../lib/trainingPlanApi";
import { useRovingRadio } from "../../../../hooks/useRovingRadio";
import { LucideIcon } from "../../../FG/LucideIcon";
import { ModalValHint } from "../../../modals/ModalValHint";
import type { GoalMode } from "../finishTime";

/** The wizard's GOAL step ("goal") — DB pace bands + custom / just-finish. */
export function GoalStep({
  goalEdit,
  library,
  goalMode,
  finish,
  setGoalMode,
  setFinish,
  setCustomFinishInput,
  customFinishInput,
  customFinishInputRef,
  customFinishInvalid,
  handleCustomFinishChange,
  handleCustomFinishBlur,
  cancelCustomFinish,
  openCustomFinish,
  band,
  nudge
}: {
  goalEdit: boolean;
  library: TrainingPlanLibrary;
  goalMode: GoalMode;
  finish: string | null;
  setGoalMode: (mode: GoalMode) => void;
  setFinish: (finish: string | null) => void;
  setCustomFinishInput: (value: string) => void;
  customFinishInput: string;
  customFinishInputRef: RefObject<HTMLInputElement>;
  customFinishInvalid: boolean;
  handleCustomFinishChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleCustomFinishBlur: () => void;
  cancelCustomFinish: () => void;
  openCustomFinish: () => void;
  band: PaceBand | null;
  /** D-15 — the Continue gate's validation nudge, rendered on-step. */
  nudge: string | null;
}) {
  const presetIndex =
    goalMode === "preset" ? library.paceBands.findIndex((b) => b.finish === finish) : -1;
  const pickPreset = (i: number) => {
    const b = library.paceBands[i];
    if (!b) return;
    setGoalMode("preset");
    setCustomFinishInput("");
    setFinish(b.finish);
  };
  const rove = useRovingRadio({
    length: library.paceBands.length,
    selectedIndex: presetIndex,
    onArrowSelect: pickPreset
  });
  return (
    <div className="fgp-wizard-panel">
      {/* Goal-edit entry (settings → "Edit" beside Goal time): be explicit
          up front that this re-applies the plan — the same-event replace
          confirm still runs before anything changes. */}
      {goalEdit && (
        <p className="fgp-modal-callout" role="note">
          Changing your goal re-applies your plan at the new paces — your
          upcoming workouts are replaced. Past weeks, boxes you&apos;ve
          already ordered, and workouts you added yourself stay put.
        </p>
      )}
      <div className="fgp-wz-goalcols">
        <div>
          <div
            className="fgp-wz-goalgrid"
            role="radiogroup"
            aria-label="Goal finish time">
            {library.paceBands.map((b, i) => {
              const on = goalMode === "preset" && b.finish === finish;
              return (
                <button
                  key={b.finish}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-pressed={on}
                  tabIndex={rove.tabIndexFor(i)}
                  onKeyDown={(e) => rove.onKeyDown(e, i)}
                  onClick={() => pickPreset(i)}
                  className={`fgp-wz-goalbtn${on ? " fgp-wz-goalbtn--on" : ""}`}>
                  {b.finish}
                </button>
              );
            })}
          </div>
          <div className="fgp-wz-goalalt">
            {goalMode === "custom" ? (
              <div
                role="radio"
                aria-checked
                className={`fgp-wz-goalbtn fgp-wz-goalbtn--alt fgp-wz-goalbtn--on fgp-wz-goalbtn--custom${customFinishInvalid ? " fgp-wz-goalbtn--invalid" : ""}`}>
                <input
                  ref={customFinishInputRef}
                  id="fgp-wz-goal-custom"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint="done"
                  placeholder="e.g. 3:25"
                  aria-label="Your goal finish time"
                  aria-invalid={customFinishInvalid}
                  pattern="\d+:\d{2}"
                  title="Hours and minutes — e.g. 3:08, 3.08, or 308"
                  value={customFinishInput}
                  onChange={handleCustomFinishChange}
                  onBlur={handleCustomFinishBlur}
                  className="fgp-wz-goalbtn__input"
                />
                <button
                  type="button"
                  className="fgp-wz-goalbtn__clear"
                  aria-label="Cancel custom finish time"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={cancelCustomFinish}>
                  <LucideIcon name="x" size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                role="radio"
                aria-checked={false}
                aria-pressed={false}
                onClick={openCustomFinish}
                className="fgp-wz-goalbtn fgp-wz-goalbtn--alt">
                Enter your own
              </button>
            )}
            <button
              type="button"
              role="radio"
              aria-checked={goalMode === "just_finish"}
              aria-pressed={goalMode === "just_finish"}
              onClick={() => {
                setGoalMode("just_finish");
                setCustomFinishInput("");
                setFinish(null);
              }}
              className={`fgp-wz-goalbtn fgp-wz-goalbtn--alt${goalMode === "just_finish" ? " fgp-wz-goalbtn--on" : ""}`}>
              Just finish
            </button>
          </div>
        </div>

        {/* D-20 — live pace preview on a tinted 2-column card with a header
            row; the peak-volume row spans both columns. */}
        {band && (
        <div className="fgp-wizard-pace-panel">
          <div className="fgp-wizard-pace-panel__head">Your training paces at this goal</div>
          <PaceRow label="Race Pace" value={band.goalPace} strong />
          <PaceRow label="Easy" value={band.easyPace} />
          <PaceRow label="Long run" value={band.longRunPace} />
          <PaceRow label="Tempo / steady" value={band.tempo} />
          {/* Marathon bands have no distinct interval pace (tempo === interval); show one row. */}
          {band.interval !== band.tempo && <PaceRow label="Intervals" value={band.interval} />}
          <PaceRow label="Recovery" value={band.recoveryPace} />
          <div className="fgp-wizard-pace-row fgp-wizard-pace-row--peak">
            <span className="fgp-wizard-pace-row__label">Peak weekly volume</span>
            <strong className="fgp-wizard-pace-row__value">{band.maxWeeklyMiles} mi</strong>
          </div>
        </div>
        )}
      </div>
      <ModalValHint message={nudge} />
    </div>
  );
}

function PaceRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="fgp-wizard-pace-row">
      <span className="fgp-wizard-pace-row__label">{label}</span>
      <strong className={`fgp-wizard-pace-row__value${strong ? " fgp-wizard-pace-row__value--strong" : ""}`}>
        {value}
      </strong>
    </div>
  );
}
